// Room geometry accessors and normalizers (CIN-15126, phase 0). Pure; relative imports only.
//
// DECISIONS
// - `roomOutline(room)` is THE way to read a room's shape: the explicit outline (same object identity, so
//   tessellation caches hit) or the implicit rectangle from width/depth. Rectangles are cached per room
//   object (WeakMap, re-checked against width/depth in case a caller mutated it) and, through
//   `rectangleOutline`, shared between room objects of equal size.
// - `normalizeRoomGeometry` is lenient on read and never throws: null / undefined / {} / non-objects give
//   the empty geometry (no `invalid` flag, that is the normal "no geometry stored" case); coordinates are
//   rounded to whole mm; clockwise outlines are reversed. Anything that then fails validation is DROPPED
//   (outline -> null, level -> removed, more than 3 levels -> extras removed) and flagged `invalid: true`
//   so the studio can show a hint. It cannot check level elevation against the room height (unknown here).
// - `levelOf`: a seat belongs to a level when it lies inside the level outline and z >= elevation - 300,
//   the highest matching level winning; otherwise 'main'.
// - `normalizeObjectProfile` returns null for anything that does not validate (a profile is all-or-nothing);
//   unknown keys are dropped.
// - `profileWorldPath` tessellates at 20 mm and applies the studio's rotation convention (clockwise degrees,
//   local +x = (cos, -sin), local +y = (sin, cos)); a closed path is returned without a repeated last point.

import { tessellateEdge } from './arc.ts';
import { ensureCcw, pointInOutline, rectangleOutline } from './outline.ts';
import { BEZEL_SIDES, OBJECT_RAILS, OBJECT_TEXTURES } from './types.ts';
import type {
    NormalizedRoomGeometry,
    ObjectProfile,
    ObjectTexture,
    OutlineVertex,
    PlanPoint,
    ProfilePoint,
    RoomGeometry,
    RoomLevel,
    RoomOutline
} from './types.ts';
import { validateGeometry, validateOutline, validateProfile } from './validate.ts';

export { rectangleOutline };

/** A level sits on the main floor's plane up to this far below its elevation (a seat in the balcony's first tier). */
const LEVEL_Z_SLACK = 300;
const UNKNOWN_HEIGHT = Number.MAX_SAFE_INTEGER;

const emptyGeometry = (): NormalizedRoomGeometry => ({ version: 1, outline: null, levels: [] });

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const roomOutlineCache = new WeakMap<object, { width: number; depth: number; outline: RoomOutline }>();

/** The room's outline: the stored one, or the rectangle from width/depth (ids r1..r4, screen edge r1). */
export function roomOutline(room: { width: number; depth: number; geometry?: RoomGeometry | null }): RoomOutline {
    const explicit = room.geometry?.outline;
    if (explicit) {
        return explicit;
    }
    const cached = roomOutlineCache.get(room);
    if (cached && cached.width === room.width && cached.depth === room.depth) {
        return cached.outline;
    }
    const outline = rectangleOutline(room.width, room.depth);
    roomOutlineCache.set(room, { width: room.width, depth: room.depth, outline });
    return outline;
}

function parseVertices(raw: unknown): OutlineVertex[] | null {
    if (!Array.isArray(raw)) return null;
    const vertices: OutlineVertex[] = [];
    for (const item of raw) {
        if (!isRecord(item) || typeof item.id !== 'string' || !finite(item.x) || !finite(item.y)) return null;
        if (item.bulge !== undefined && item.bulge !== null && !finite(item.bulge)) return null;
        const bulge = finite(item.bulge) && item.bulge !== 0 ? item.bulge : undefined;
        vertices.push({
            id: item.id,
            x: Math.round(item.x) + 0,
            y: Math.round(item.y) + 0,
            ...(bulge !== undefined ? { bulge } : {})
        });
    }
    return vertices;
}

function parseOutline(raw: unknown): RoomOutline | null {
    if (!isRecord(raw) || typeof raw.screenEdge !== 'string') return null;
    const vertices = parseVertices(raw.vertices);
    if (!vertices) return null;
    const outline = ensureCcw({ screenEdge: raw.screenEdge, vertices });
    return validateOutline(outline) ? null : outline;
}

function parseLevel(raw: unknown, outline: RoomOutline | null): RoomLevel | null {
    if (!isRecord(raw) || typeof raw.id !== 'string' || !isRecord(raw.outline)) return null;
    const vertices = parseVertices(raw.outline.vertices);
    if (!vertices || !finite(raw.elevation) || !finite(raw.slabThickness)) return null;
    if (!Array.isArray(raw.frontEdges) || raw.frontEdges.some((id) => typeof id !== 'string')) return null;
    const level: RoomLevel = {
        id: raw.id,
        name: typeof raw.name === 'string' ? raw.name : raw.id,
        elevation: Math.round(raw.elevation),
        slabThickness: Math.round(raw.slabThickness),
        outline: ensureCcw({ vertices }),
        frontEdges: raw.frontEdges as string[]
    };
    const error = validateGeometry({ version: 1, outline, levels: [level] }, { height: UNKNOWN_HEIGHT });
    return error ? null : level;
}

/** Turns whatever came off the wire into a usable `RoomGeometry`; see the module header for the rules. */
export function normalizeRoomGeometry(raw: unknown): NormalizedRoomGeometry {
    if (!isRecord(raw)) {
        return emptyGeometry();
    }
    const result = emptyGeometry();
    let invalid = false;
    if (raw.version !== undefined && raw.version !== 1) {
        return { ...result, invalid: true };
    }
    if (raw.outline !== undefined && raw.outline !== null) {
        result.outline = parseOutline(raw.outline);
        invalid = result.outline === null;
    }
    const rawLevels = Array.isArray(raw.levels) ? raw.levels : [];
    if (raw.levels !== undefined && !Array.isArray(raw.levels)) invalid = true;
    if (rawLevels.length > 3) invalid = true;
    const seen = new Set<string>();
    for (const item of rawLevels.slice(0, 3)) {
        const level = parseLevel(item, result.outline);
        if (!level || seen.has(level.id)) {
            invalid = true;
            continue;
        }
        seen.add(level.id);
        result.levels.push(level);
    }
    return invalid ? { ...result, invalid: true } : result;
}

/** 'main' or the id of the level a seat stands on (see the module header). */
export function levelOf(g: RoomGeometry | null | undefined, seat: { x: number; y: number; z: number }): string {
    let best: RoomLevel | null = null;
    for (const level of g?.levels ?? []) {
        if (
            seat.z >= level.elevation - LEVEL_Z_SLACK &&
            pointInOutline(level.outline, seat) &&
            (!best || level.elevation > best.elevation)
        ) {
            best = level;
        }
    }
    return best ? best.id : 'main';
}

const isCap = (v: unknown): v is NonNullable<ObjectProfile['cap']> =>
    v === 'none' || v === 'handrail' || v === 'brass' || v === 'glass';
const isLed = (v: unknown): v is NonNullable<ObjectProfile['led']> =>
    v === 'none' || v === 'top' || v === 'base' || v === 'face';
const isRiser = (v: unknown): v is NonNullable<ObjectProfile['riser']> =>
    v === 'plain' || v === 'grille' || v === 'led';

/** A valid `ObjectProfile` or null. Coordinates are rounded to whole mm; unknown keys are dropped. */
export function normalizeObjectProfile(raw: unknown): ObjectProfile | null {
    if (
        !isRecord(raw) ||
        (raw.version !== undefined && raw.version !== 1) ||
        !isRecord(raw.path) ||
        !Array.isArray(raw.path.points)
    ) {
        return null;
    }
    const points: ProfilePoint[] = [];
    for (const item of raw.path.points) {
        if (!isRecord(item) || !finite(item.x) || !finite(item.y)) return null;
        if (item.bulge !== undefined && item.bulge !== null && !finite(item.bulge)) return null;
        const bulge = finite(item.bulge) && item.bulge !== 0 ? item.bulge : undefined;
        points.push({
            x: Math.round(item.x) + 0,
            y: Math.round(item.y) + 0,
            ...(bulge !== undefined ? { bulge } : {})
        });
    }
    const profile: ObjectProfile = { version: 1, path: { closed: raw.path.closed === true, points } };
    const { steps, openings } = raw;
    if (isRecord(steps) && finite(steps.count) && finite(steps.tread)) {
        const edges =
            steps.edges === 'front' || steps.edges === 'all'
                ? steps.edges
                : Array.isArray(steps.edges) && steps.edges.every(finite)
                  ? (steps.edges as number[])
                  : 'front';
        profile.steps = { count: Math.round(steps.count), tread: Math.round(steps.tread), edges };
    }
    if (Array.isArray(openings)) {
        profile.openings = openings.flatMap((o) =>
            isRecord(o) && finite(o.at) && finite(o.width) ? [{ at: Math.round(o.at), width: Math.round(o.width) }] : []
        );
    }
    if (finite(raw.thickness)) profile.thickness = Math.round(raw.thickness);
    if (isCap(raw.cap)) profile.cap = raw.cap;
    if (isLed(raw.led)) profile.led = raw.led;
    if (isRiser(raw.riser)) profile.riser = raw.riser;
    if (finite(raw.cupholderSpacing)) profile.cupholderSpacing = Math.round(raw.cupholderSpacing);
    if (typeof raw.cupholders === 'boolean') profile.cupholders = raw.cupholders;
    if (typeof raw.color === 'string' && /^#[0-9a-f]{6}$/i.test(raw.color)) profile.color = raw.color as `#${string}`;
    if ((OBJECT_TEXTURES as readonly unknown[]).includes(raw.texture)) profile.texture = raw.texture as ObjectTexture;
    if (isRecord(raw.bezel) && finite(raw.bezel.width) && finite(raw.bezel.depth)) {
        const bezel: NonNullable<ObjectProfile['bezel']> = {
            width: Math.round(raw.bezel.width),
            depth: Math.round(raw.bezel.depth)
        };
        for (const side of BEZEL_SIDES) {
            const value = raw.bezel[side];
            if (finite(value)) bezel[side] = Math.round(value);
        }
        profile.bezel = bezel;
    }
    if ((OBJECT_RAILS as readonly unknown[]).includes(raw.rails)) profile.rails = raw.rails as ObjectProfile['rails'];
    return validateProfile(profile) ? null : profile;
}

/** The profile's path in plan space (mm) for an object at (x, y) rotated `rotation` degrees clockwise. */
export function profileWorldPath(
    profile: ObjectProfile,
    object: { x: number; y: number; rotation: number }
): PlanPoint[] {
    const { points, closed } = profile.path;
    const local: PlanPoint[] = [];
    const count = closed ? points.length : points.length - 1;
    for (let i = 0; i < count; i += 1) {
        const a = points[i];
        const b = points[(i + 1) % points.length];
        local.push(...tessellateEdge(a, b, a.bulge ?? 0).slice(0, -1));
    }
    if (!closed && points.length > 0) {
        local.push({ x: points[points.length - 1].x, y: points[points.length - 1].y });
    }
    const rad = (object.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return local.map((p) => ({ x: object.x + p.x * cos + p.y * sin, y: object.y - p.x * sin + p.y * cos }));
}
